/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: organizations entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the organizations table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the organizations API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, name, short_name, contact_name, phone, website, created_by, created_at, updated_by, updated_at, non_member_surcharge, allow_non_member_buddy_group, membership_url, member_fee_enabled, allow_applications, default_entry_fee_prize_portion, default_entry_fee_prize_portion_is_percentage, default_entry_fee_prize_portion_is_fee_deduction]
 * Searchable fields: []
 * Filterable fields: [id]
 */

import { httpClient } from '../http-client.js';
import type { UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  OrganizationsRow,
  OrganizationsInsert,
  OrganizationsUpdate
} from '../../types/generated_schemas/generated-organizations-schema.js';

export interface OrganizationsQueryOptions extends UnifiedQueryOptions {}

export interface OrganizationsResponse {
  data: OrganizationsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for organizations table operations
 *
 * @description Provides type-safe access to organizations API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class OrganizationsClient {
  private readonly basePath = '/organizations';

  /**
   * Get all organizations records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of organizations records
   */
  async getAll(options?: OrganizationsQueryOptions): Promise<OrganizationsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<OrganizationsResponse>(url);
    return response.data;
  }

  /**
   * Get a single organizations record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the organizations record
   */
  async getById(id: string): Promise<OrganizationsRow> {
    const response = await httpClient.get<{
      data: OrganizationsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new organizations record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created organizations record
   */
  async create(data: OrganizationsInsert): Promise<OrganizationsRow> {
    const response = await httpClient.post<{
      data: OrganizationsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing organizations record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated organizations record
   */
  async update(id: string, data: OrganizationsUpdate): Promise<OrganizationsRow> {
    const response = await httpClient.put<{
      data: OrganizationsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a organizations record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple organizations records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created organizations records
   */
  async createMany(data: OrganizationsInsert[]): Promise<OrganizationsRow[]> {
    const response = await httpClient.post<{
      data: OrganizationsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple organizations records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search organizations with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: OrganizationsQueryOptions
  ): Promise<OrganizationsResponse> {
    throw new Error('Search operations are not available for organizations - no searchable fields configured');
  }

  /**
   * Get count of organizations records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a organizations record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'organizations',
      publicFields: ['id', 'name', 'short_name', 'contact_name', 'phone', 'website', 'created_by', 'created_at', 'updated_by', 'updated_at', 'non_member_surcharge', 'allow_non_member_buddy_group', 'membership_url', 'member_fee_enabled', 'allow_applications', 'default_entry_fee_prize_portion', 'default_entry_fee_prize_portion_is_percentage', 'default_entry_fee_prize_portion_is_fee_deduction'],
      searchableFields: [],
      filterableFields: ['id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'member_fee_enabled', 'allow_applications', 'default_entry_fee_prize_portion', 'default_entry_fee_prize_portion_is_percentage', 'default_entry_fee_prize_portion_is_fee_deduction'],
    };
  }
}

// Export singleton instance for convenience
export const organizationsClient = new OrganizationsClient();
